import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { errorText } from '../../utils/errorText';
import { photoTypeLabel } from '../../utils/photoCapture';
import { resolvePhotoUrl } from '../../utils/photoUrl';
import { displayName } from '../../utils/userLabel';
import PhotoViewer from '../PhotoViewer';
import ConfirmDeleteModal from '../ConfirmDeleteModal';

/*
 * One pin, in full (GET /map/points/{id}, routes/map_points.py): where and when,
 * who saw it, what it is and whether that is verified, and every photograph.
 *
 * And what can be done with it from here:
 *   * an observation becomes a collection by minting a specimen code for it
 *     (POST /specimens {sample_id}); the server decides, the button is only
 *     offered where it would agree (`actions.convert_to_collection`). One
 *     record has one specimen: if another got there first the server answers
 *     409 origin_registered, naming its code to whoever may see it;
 *   * a public sighting is reviewed here, where the Public Sightings page used
 *     to do it (routes/public_survey.py, collection managers and internal
 *     experts): confirmed (`actions.confirm_public_sighting`), deleted
 *     (`actions.delete_public_sighting`, soft: a manager can restore it), and
 *     once confirmed and its specimen has reached the lab, registered as a lab
 *     sample (`actions.register_public_sample`). Deleting and registering ask
 *     first. A reviewer sees who sent it (`submitter_email`);
 *   * a staff record opens in Collections, where everything else is edited.
 *
 * A sighting sent with no location has no pin; pages/NationalMap.js lists
 * those for reviewers beside the map.
 *
 * A pin still on this device (`point.local`) has nothing on the server yet:
 * it shows what the device holds and that it is waiting to be sent.
 *
 * Photos, substrate and region read as they do in Collections: the view from
 * common:photoType, and a photo opened full size says when and where it was
 * taken where it carries those tags (PhotoViewer, as PhotoGallery shows them).
 *
 * Any pin, the device's own included, can be made the TARGET to walk to
 * (`onSetTarget`, utils/activeTarget.js): offered whether or not its details
 * have loaded, since walking to it needs only where it is. While it is the
 * target (`isTarget`) the same place offers to clear it. A pin documented
 * since (records made with it as their target, migration 0067) says how often.
 */
export default function PointDrawer({ point, onClose, onChanged, isTarget = false, onSetTarget, onClearTarget }) {
  const { t } = useTranslation(['map', 'common', 'collections', 'regions']);
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [viewing, setViewing] = useState(null);
  // 'delete' | 'register' while ConfirmDeleteModal asks; its own error line.
  const [confirming, setConfirming] = useState(null);
  const [confirmError, setConfirmError] = useState('');
  const panelRef = useRef(null);

  const load = useCallback(async () => {
    if (point.local) return;
    setError('');
    try {
      setDetail((await api.get(`/map/points/${point.id}`)).data);
    } catch (e) {
      setError(errorText(e));
    }
  }, [point]);
  useEffect(() => { setDetail(null); setMessage(''); load(); }, [load]);
  useEffect(() => { panelRef.current?.focus?.(); }, [point]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (confirming) {
        if (!busy) setConfirming(null);
      } else if (viewing) {
        setViewing(null);
      } else {
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, viewing, confirming, busy]);

  const shown = detail || point;
  const act = async (work, done) => {
    setBusy(true);
    setError('');
    try {
      const result = await work();
      setMessage(done(result));
      await load();
      onChanged?.();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const convert = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await api.post('/specimens', { sample_id: point.id });
      setMessage(t('drawer.converted', { code: res.data?.specimen_code || '' }));
      await load();
      onChanged?.();
    } catch (e) {
      const data = e?.response?.data || {};
      if (e?.response?.status === 409 && data.code === 'origin_registered') {
        // Already a collection: read it again, so the offer goes away.
        await load();
        onChanged?.();
        setError(data.specimen_code ? t('drawer.originRegistered', { code: data.specimen_code })
          : t('drawer.originRegisteredHidden'));
      } else {
        setError(errorText(e));
      }
    } finally {
      setBusy(false);
    }
  };
  const confirmSighting = () => act(
    () => api.put(`/public-sightings/${point.id}/confirm`),
    () => t('drawer.sightingConfirmed'));
  const ask = (kind) => { setConfirmError(''); setConfirming(kind); };
  const runConfirmed = async () => {
    setBusy(true);
    setConfirmError('');
    try {
      if (confirming === 'delete') {
        await api.delete(`/public-sightings/${point.id}`);
        setConfirming(null);
        onChanged?.();
        onClose();
        return;
      }
      await api.put(`/public-sightings/${point.id}/register-lab-sample`);
      setConfirming(null);
      setMessage(t('drawer.registered'));
      await load();
      onChanged?.();
    } catch (e) {
      setConfirmError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const when = shown.collected_at ? new Date(shown.collected_at) : null;
  const rows = [
    ['coordinates', `${Number(shown.lat).toFixed(5)}, ${Number(shown.lng).toFixed(5)}`],
    ['when', when && !Number.isNaN(when.getTime()) ? when.toLocaleString() : null],
    ['observer', shown.observer && displayName(shown.observer, t)],
    // Public sightings, to their reviewers only (routes/map_points.py).
    ['submitter', shown.submitter_email],
    ['species', shown.taxon_name || t('drawer.unidentified')],
    ['code', shown.code],
    ['region', shown.region ? t(shown.region, { ns: 'regions', defaultValue: shown.region }) : shown.locality_text],
    ['substrate', shown.substrate ? t(`collections:substrate.${shown.substrate}`, { defaultValue: shown.substrate }) : null],
    ['notes', shown.notes],
  ].filter(([, v]) => v);
  const ident = detail?.identification;

  return (
    <aside ref={panelRef} tabIndex={-1} role="dialog" aria-modal="false" aria-labelledby="drawer-title"
      style={drawer} data-testid="point-drawer">
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
        <h3 id="drawer-title" style={{ margin: 0, fontSize: 17 }}>
          <span style={badge(point.local ? 'local' : shown.source === 'public' ? 'public' : shown.kind)}>
            {t(point.local ? 'kind.local' : shown.source === 'public' ? 'kind.public' : `kind.${shown.kind}`)}
          </span>{' '}
          {shown.taxon_name || t('drawer.unidentified')}
        </h3>
        <button type="button" onClick={onClose} aria-label={t('common:close')} title={t('tip.drawer.close')}
          style={closeBtn} data-testid="drawer-close">✕</button>
      </div>

      {!point.local && (
        <p style={{ margin: '6px 0' }} data-testid="drawer-verification">
          <span style={verifyBadge(shown.verification)}>{t(`verification.${shown.verification}`)}</span>
        </p>
      )}
      {point.local && <p style={muted} data-testid="drawer-local">{t('drawer.waiting')}</p>}

      <dl style={dl}>
        {rows.map(([k, v]) => (
          <div key={k} style={{ display: 'contents' }}>
            <dt style={{ fontWeight: 600 }}>{t(`drawer.${k}`)}</dt>
            <dd style={{ margin: 0, overflowWrap: 'anywhere' }}>{v}</dd>
          </div>
        ))}
      </dl>

      {ident && (
        <p style={{ ...muted, marginTop: 8 }} data-testid="drawer-identification">
          {t('drawer.identifiedBy', { name: ident.identified_by || '—', taxon: ident.taxon_name || '—',
            method: ident.method || '—' })}
          {ident.expert_approval ? ` · ${t('drawer.expertApproved')}` : ''}
        </p>
      )}

      <h4 style={{ margin: '12px 0 6px' }}>{t('drawer.photos', { count: point.local ? (point.photo_count || 0)
        : (detail?.photos?.length ?? shown.photo_count ?? 0) })}</h4>
      {!detail && !point.local && !error && <p style={muted}>{t('common:loading')}</p>}
      {point.local && point.thumbnail && <img src={point.thumbnail} alt="" style={thumbImg} />}
      {detail?.photos?.length > 0 && (
        <div style={gallery} data-testid="drawer-gallery">
          {detail.photos.map(p => (
            <button key={p.photo_id} type="button" onClick={() => setViewing(p)} style={thumbBtn}
              aria-label={photoTypeLabel(t, p.photo_type)} title={t('tip.drawer.photo')}
              data-testid={`drawer-photo-${p.photo_id}`}>
              <img src={resolvePhotoUrl(p.thumbnail_url || p.s3_url)} alt="" loading="lazy" style={thumbImg} />
              <span style={caption}>{photoTypeLabel(t, p.photo_type)}</span>
            </button>
          ))}
        </div>
      )}

      {detail?.documented_count > 0 && (
        <p style={muted} data-testid="drawer-documented">{t('drawer.documented', { count: detail.documented_count })}</p>
      )}

      {onSetTarget && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 12 }}>
          {isTarget ? (
            <>
              <span style={{ ...muted, fontWeight: 600 }} data-testid="drawer-is-target">🎯 {t('target.current')}</span>
              <button type="button" onClick={() => onClearTarget?.()} style={btn('var(--c-text-muted)')}
                title={t('tip.drawer.targetClear')} data-testid="drawer-clear-target">{t('target.clear')}</button>
            </>
          ) : (
            <button type="button" onClick={() => onSetTarget({ ...shown, id: point.id, local: !!point.local })} style={btn('#d81b60')}
              title={t('tip.drawer.target')} data-testid="drawer-set-target">🎯 {t('target.set')}</button>
          )}
        </div>
      )}

      {error && <p role="alert" style={{ color: 'var(--c-danger-fg)' }}>{error}</p>}
      {message && <p style={{ color: 'var(--c-success-fg)' }} data-testid="drawer-message">{message}</p>}

      {detail && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
          {detail.actions?.convert_to_collection && (
            <button type="button" disabled={busy} onClick={convert} style={btn('var(--c-success)')}
              title={t('tip.drawer.convert')} data-testid="drawer-convert">{t('drawer.convert')}</button>
          )}
          {detail.actions?.confirm_public_sighting && (
            <button type="button" disabled={busy} onClick={confirmSighting} style={btn('var(--c-success)')}
              title={t('tip.drawer.confirmSighting')} data-testid="drawer-confirm-sighting">
              {t('drawer.confirmSighting')}</button>
          )}
          {detail.actions?.register_public_sample && (
            <button type="button" disabled={busy} onClick={() => ask('register')} style={btn('var(--c-success)')}
              title={t('tip.drawer.register')} data-testid="drawer-register">{t('drawer.registerPublic')}</button>
          )}
          {detail.source === 'field' && (
            <button type="button" onClick={() => navigate('/collections', { state: { openCollectionId: point.id } })}
              title={t('tip.drawer.open')} style={btn('var(--c-info)')} data-testid="drawer-open">
              {t('drawer.openRecord')}</button>
          )}
          {detail.actions?.delete_public_sighting && (
            <button type="button" disabled={busy} onClick={() => ask('delete')} style={btn('var(--c-danger)')}
              title={t('tip.drawer.deleteSighting')} data-testid="drawer-delete-sighting">
              {t('drawer.deleteSighting')}</button>
          )}
        </div>
      )}

      <ConfirmDeleteModal
        open={!!confirming}
        title={confirming === 'delete' ? t('drawer.deleteSightingTitle') : t('drawer.registerTitle')}
        consequences={[confirming === 'delete' ? t('drawer.deleteSightingConsequence')
          : t('drawer.registerConsequence')]}
        deleting={busy}
        error={confirmError}
        confirmLabel={confirming === 'register' ? t('drawer.registerPublic') : undefined}
        confirmingLabel={confirming === 'register' ? t('drawer.registering') : undefined}
        confirmColor={confirming === 'register' ? 'var(--c-success)' : undefined}
        onConfirm={runConfirmed}
        onCancel={() => setConfirming(null)}
      />

      {/* The app's photo viewer: pinch, wheel, drag and the zoom buttons
          magnify; previous/next step through the point's photos. */}
      {viewing && detail?.photos?.length > 0 && (
        <PhotoViewer photos={detail.photos} testId="drawer-lightbox"
          index={Math.max(0, detail.photos.findIndex(p => p.photo_id === viewing.photo_id))}
          onIndex={i => setViewing(detail.photos[i])} onClose={() => setViewing(null)} />
      )}
    </aside>
  );
}

const PIN = { observation: '#1e88e5', collection: '#2e7d32', public: '#ef6c00', local: '#757575' };
const drawer = { position: 'absolute', top: 0, bottom: 0, insetInlineEnd: 0, zIndex: 1000, width: 'min(420px, 100%)',
  overflowY: 'auto', background: 'var(--c-surface)', color: 'var(--c-text-fg)', padding: 16,
  boxShadow: '0 0 24px rgba(0,0,0,.25)', outline: 'none', boxSizing: 'border-box' };
const closeBtn = { background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--c-text-fg)', minWidth: 44, minHeight: 44 };
const badge = (kind) => ({ fontSize: 11, padding: '2px 8px', borderRadius: 999, color: '#fff', background: PIN[kind] || PIN.observation,
  verticalAlign: 'middle' });
const verifyBadge = (v) => ({ fontSize: 12, padding: '2px 8px', borderRadius: 999, border: '1px solid var(--c-border)',
  background: v === 'verified' ? 'var(--c-success-soft)' : 'var(--c-bg-subtle)',
  color: v === 'verified' ? 'var(--c-success-fg)' : 'var(--c-text-fg)' });
const muted = { fontSize: 13, color: 'var(--c-text-muted-fg)' };
const dl = { display: 'grid', gridTemplateColumns: 'minmax(90px, auto) 1fr', gap: '4px 12px', fontSize: 14, margin: '8px 0 0' };
const gallery = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 8 };
const thumbBtn = { padding: 0, border: '1px solid var(--c-border)', borderRadius: 8, background: 'none', cursor: 'zoom-in',
  overflow: 'hidden', display: 'flex', flexDirection: 'column', color: 'var(--c-text-fg)' };
const thumbImg = { display: 'block', width: '100%', height: 110, objectFit: 'cover' };
const caption = { fontSize: 11, padding: '2px 4px' };
const btn = (bg) => ({ padding: '10px 14px', minHeight: 44, borderRadius: 6, border: 'none', background: bg,
  color: '#fff', fontSize: 14, cursor: 'pointer' });
